import type { Drawing, LotteryDrawResult } from '../../lottery-results/types';
import type { ExtraGameName, LotteryName } from '../types';

type LotteryResultsMapper = (results: Drawing) => LotteryDrawResult;

/**
 * Creates a mapper for extra games in Irish Lotto.
 *
 * @param gameName - The name of the extra game
 * @returns A mapper function that returns the numbers and extra numbers for the extra game
 */
const createIrishLottoExtraGameMapper =
  (gameName: ExtraGameName): LotteryResultsMapper =>
  (results: Drawing) => {
    const extraGame = results.extraGames?.find(game => game.name === gameName);
    return {
      numbers: [...(extraGame?.numbers ?? []), ...(extraGame?.extraNumbers ?? [])],
    };
  };

/**
 * Lottery-specific mappers for drawing results.
 *
 * Different lotteries have different rules for how results should be processed for matching:
 * - irishLotto: Combines main numbers and extra numbers (bonus ball) for matching
 * - Other lotteries: Keep numbers and extra numbers separate for matching
 */
const lotteryResultsMappers: Partial<Record<LotteryName, Partial<Record<LotteryName | ExtraGameName, LotteryResultsMapper>>>> = {
  irishLotto: {
    irishLotto: (results: Drawing) => ({
      numbers: [...results.numbers, ...(results.extraNumbers ?? [])],
    }),
    lottoPlus1: createIrishLottoExtraGameMapper('lottoPlus1'),
    lottoPlus2: createIrishLottoExtraGameMapper('lottoPlus2'),
  },
};

const defaultResultsMapper: LotteryResultsMapper = (results: Drawing) => ({
  numbers: results.numbers,
  extraNumbers: results.extraNumbers ?? [],
});

/**
 * Maps lottery drawing results according to lottery-specific matching rules.
 *
 * @param lotteryName - The lottery identifier
 * @param gameName - The game name (main game or extra game)
 * @param results - The drawing results from the API
 * @returns Mapped numbers and extraNumbers ready for matching against player's lines
 *
 */
function mapLotteryResults(lotteryName: LotteryName, gameName: LotteryName | ExtraGameName, results: Drawing): LotteryDrawResult {
  const lotteryMappers = lotteryResultsMappers[lotteryName];
  const mapper = lotteryMappers?.[gameName] ?? defaultResultsMapper;
  return mapper(results);
}

export { defaultResultsMapper, lotteryResultsMappers, mapLotteryResults };
export type { LotteryResultsMapper };
